import React, { useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { GoogleInitConfig, GoogleButtonConfig, GoogleCredentialResponse } from '../../types/auth';
import './GoogleLoginButton.css';

interface GoogleLoginButtonProps {
  onSuccess?: () => void;
  onError?: (error: string) => void;
  text?: 'signin_with' | 'signup_with' | 'continue_with' | 'signin';
  theme?: 'outline' | 'filled_blue' | 'filled_black';
  size?: 'large' | 'medium' | 'small';
  className?: string;
}

export const GoogleLoginButton: React.FC<GoogleLoginButtonProps> = ({
  onSuccess,
  onError,
  text = 'signin_with',
  theme = 'outline',
  size = 'large',
  className = ''
}) => {
  const { googleLogin } = useAuth();
  const buttonRef = useRef<HTMLDivElement>(null);
  const isInitialized = useRef(false);

  // Check if Google Client ID is configured
  const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
  const isConfigured = clientId && clientId !== 'placeholder-get-from-google-console';

  const handleCredentialResponse = useCallback(async (response: GoogleCredentialResponse) => {
    try {
      await googleLogin(response.credential);
      onSuccess?.();
    } catch (error: any) {
      console.error('Google login failed:', error);
      onError?.(error.message || 'Google login failed');
    }
  }, [googleLogin, onSuccess, onError]);

  const initializeGoogleAuth = useCallback(() => {
    if (!window.google || isInitialized.current || !isConfigured || !clientId) return;

    const config: GoogleInitConfig = {
      client_id: clientId,
      callback: handleCredentialResponse,
    };

    try {
      window.google.accounts.id.initialize(config);
      
      if (buttonRef.current) {
        const buttonConfig: GoogleButtonConfig = {
          theme,
          size,
          text,
          shape: 'rectangular',
          logo_alignment: 'left',
        };
        
        window.google.accounts.id.renderButton(buttonRef.current, buttonConfig);
        isInitialized.current = true;
      }
    } catch (error) {
      console.error('Error initializing Google auth:', error);
      onError?.('Failed to initialize Google authentication');
    }
  }, [theme, size, text, onError, handleCredentialResponse, clientId, isConfigured]);

  useEffect(() => {
    if (!isConfigured) {
      console.warn('Google Client ID not configured. Google login button will not be displayed.');
      return;
    }

    // Check if Google Identity Services script is loaded
    if (window.google) {
      initializeGoogleAuth();
    } else {
      // Wait for script to load
      const checkGoogle = setInterval(() => {
        if (window.google) {
          clearInterval(checkGoogle);
          initializeGoogleAuth();
        }
      }, 100);

      // Cleanup interval after 10 seconds
      setTimeout(() => clearInterval(checkGoogle), 10000);

      return () => clearInterval(checkGoogle);
    }
  }, [initializeGoogleAuth, isConfigured]);

  // Don't render anything if not configured
  if (!isConfigured) {
    return null;
  }

  return (
    <div 
      className={`google-login-container ${className}`} 
      style={{ 
        width: '100%', 
        display: 'flex', 
        justifyContent: 'center',
        alignItems: 'center',
        textAlign: 'center'
      }}
    >
      <div 
        ref={buttonRef} 
        className="google-login-button" 
        style={{ 
          width: '100%', 
          maxWidth: '400px',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          textAlign: 'center',
          margin: '0 auto'
        }} 
      />
    </div>
  );
};